import React, { useEffect } from "react";
import moment from "moment";
import AddIcon from "@material-ui/icons/Add";
import EmailIcon from "@material-ui/icons/Email";

import {
  ConsultantsMassList,
  GetConsultantsByWeeks,
  NodeRefFragment,
  NodeType
} from "@graphql";
import { store } from "@store";
import locale from "@bonliva-crm/locale";

import ActionBar from "@search/ActionBar";
import { searchFactory } from "@search/Search";
import { GeneralSearchStore, GeneralSearchData } from "@search/GeneralSearchStore";
import GroupHeadline from "@components/GroupHeadline";
import { getConsultantsFilter } from "../utils";
import ConsultantCard from "../ConsultantCard/ConsultantCard";
import { getConsultantsByWeeksFilter } from "./getConsultantsByWeeksFilter";
import Fab from "@material-ui/core/Fab";
import { ContactListColumn } from "@containers/MassContactActions/components/ContactList";
import { MassActionsLocation } from "@containers/MassContactActions/MassActionsLocation";

type Record = GetConsultantsByWeeks.Return["consultantsByWeeks"][0];
const Search = searchFactory();

type Props = {
  onItemOpen: (id: string, search: string) => any;
  onNewItem: (prefix?: string) => any;
  onNodeClick: (nodeRef: Partial<NodeRefFragment>) => void;
};

export function AvailableConsultantSearch(props: Props) {
  useEffect(() => {
    store().generalSearchStore.isMoreDisabledInSticky = true;
    return () => {
      store().generalSearchStore.isMoreDisabledInSticky = false;
    };
  });

  const fetchData = (skip: number, store: GeneralSearchStore) => {
    const filter = getConsultantsByWeeksFilter(store.searchParams, {
      areasOfExp: store.areasOfExp
    });

    return GetConsultantsByWeeks.func(
      {
        filter,
        skip,
        limit: 20,
        withEvents: Boolean(filter.fullTextSearch) && Boolean(filter.text)
      },
      { fetchPolicy: "network-only" }
    )
      .then((result) => {
        return result.data.consultantsByWeeks;
      })
      .catch((e) => {
        store.rootStore.snackbars.error(`${locale.SearchFailed}.\nReason: ${e}`);

        return [];
      });
  };

  const getGroup = (data) => {
    return moment.utc(data.Week).format("W (YYYY)");
  };

  const renderGroup = (data: Record) => {
    const m = moment.utc(data.Week);
    const year = m.format("YYYY");
    const week = m.format("W");
    return <GroupHeadline mainText={`${locale.Week} ${week}`} smallText={year} />;
  };

  const massActions = (search: GeneralSearchData, searchStore: GeneralSearchStore) => {
    const filter = getConsultantsFilter(
      { ...search, availability: null },
      {
        areasOfExp: searchStore.areasOfExp,
        includeAvailabilityDateRange: true
      }
    );
    filter.fitness = "fit";

    const columns: ContactListColumn[] = ["CellPhone", "Email", "ResponsiblePerson"];

    if (store().user.hasPermission("CanDeleteConsultants")) {
      columns.push("Delete");
    }

    const location = new MassActionsLocation({
      options: {
        nodeType: NodeType.Consultant,
        allSelected: true,
        getData: () =>
          ConsultantsMassList.func(
            { filter, limit: filter.location?.zipOrCity ? 200 : 1000 },
            { fetchPolicy: "network-only" }
          ).then((res) => ({
            items: res.data.consultants.items,
            total: res.data.consultants.total
          }))
      },
      columns
    });

    store().navigation.open("mass_actions", location);
  };

  const { onNodeClick } = props;
  const { user } = store();
  const { myProfile } = user;

  return (
    <Search
      nodeType={NodeType.Consultant}
      fetchData={fetchData}
      title={locale.Consultant}
      offsetPulldownMenuTop={48}
      hideClearFilter
      hideSearchBar
      renderDetail={({ item, search }) => (
        <ConsultantCard
          consultantId={(item as Record).Consultant._id}
          highlightSearch={search.text.trim()}
          onClick={(e) => {
            const elem = e.target as HTMLElement;
            if (elem.tagName !== "A") {
              props.onItemOpen(
                (item as Record).Consultant._id,
                store().generalSearchStore.searchParams.text
              );
            }
          }}
          withEvents={false}
          onResponsiblePersonClick={() =>
            onNodeClick({
              NodeID: (item as Record).Consultant.ResponsiblePerson.PersonId,
              NodeType: NodeType.User
            })
          }
        />
      )}
      renderActionBar={(data, searchStore) => {
        return (
          <ActionBar
            style={{ padding: "8px 16px" }}
            actions={[
              <Fab
                key="new"
                color="primary"
                size="small"
                title={locale.New}
                onClick={() => props.onNewItem("/consultant")}
              >
                <AddIcon />
              </Fab>,

              <Fab
                key="mass_actions"
                data-test="mass-actions-open"
                onClick={() => {
                  massActions(data, searchStore);
                }}
                size="small"
                color="primary"
                title={locale.CreateList}
              >
                <EmailIcon />
              </Fab>
            ]}
          />
        );
      }}
      renderHeader={() => null}
      group={{
        by: () => getGroup,
        render: renderGroup,
        options: []
      }}
      noResultsMessage={locale["Call your consultants!"]}
    >
      {(p) => [
        <>
          {p.responsiblePerson} {p.consultants}
          {p.expertiseWithExperienceAndExclusive} {p.specializations}
          {p.from} {p.to}
        </>,
        <>
          {p.availableForWorkInCountries} {p.region} {p.workshifts}
          {p.knownSystems} {p.competitors} {p.initialContact}
          {p.lastContact} {p.lastContactType} {p.qualification}
          {p.fitness} {p.seniorAndDrivingLicense}
          {myProfile.Permissions.CanDeleteConsultants ? p.status : null}
        </>
      ]}
    </Search>
  );
}

export default AvailableConsultantSearch;
